import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAdmin } from '../../context/AdminContext';
import { useBlog } from '../../context/BlogContext';
import AdminBlogHeader from '../../components/adminblog/AdminBlogHeader';
import BlockEditor from '../../components/adminblog/BlockEditor';
import BlockConfigPanel from '../../components/adminblog/BlockConfigPanel';
import PostSettingsPanel from '../../components/adminblog/PostSettingsPanel';
import BlogBlockRenderer from '../../components/blog/BlogBlockRenderer';
import '../../styles/AdminBlog.css';
import '../../styles/Blog.css';

export default function AdminBlogEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAdmin();
  const { getPost, updatePost, refreshPosts } = useBlog();

  const [post, setPost] = useState(null);
  const [selectedBlockId, setSelectedBlockId] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) navigate('/adminblog');
  }, [isAuthenticated, authLoading, navigate]);

  // Asegura que la lista completa (con borradores) esté cargada al abrir el
  // editor directamente por URL o tras recargar la página.
  useEffect(() => {
    if (isAuthenticated) refreshPosts();
  }, [isAuthenticated, refreshPosts]);

  useEffect(() => {
    const found = getPost(id);
    if (found) {
      setPost({ ...found });
    }
  }, [id, getPost]);

  const handleSave = useCallback(async () => {
    if (!post) return;
    setSaving(true);
    try {
      await updatePost(post.id, post);
      setMessage({ type: 'success', text: 'Post saved successfully' });
      setTimeout(() => setMessage(null), 3000);
    } catch (e) {
      setMessage({ type: 'error', text: 'No se pudo guardar. ¿Sesión caducada?' });
    } finally {
      setSaving(false);
    }
  }, [post, updatePost]);

  const handlePostChange = (updatedPost) => {
    setPost(updatedPost);
  };

  const handleBlocksChange = (newBlocks) => {
    setPost(prev => ({ ...prev, blocks: newBlocks }));
  };

  const handleBlockUpdate = (updatedBlock) => {
    setPost(prev => ({
      ...prev,
      blocks: prev.blocks.map(b => b.id === updatedBlock.id ? updatedBlock : b),
    }));
  };

  const selectedBlock = post?.blocks?.find(b => b.id === selectedBlockId) || null;
  const sortedBlocks = post ? [...(post.blocks || [])].sort((a, b) => a.order - b.order) : [];

  if (authLoading) return <div className="adminblog-loading">Loading...</div>;
  if (!post) return <div className="adminblog-loading">Post not found</div>;

  return (
    <div className="adminblog-page">
      <AdminBlogHeader
        title={post.title || 'Edit Post'}
        subtitle={post.status === 'draft' ? 'Draft' : 'Published'}
        onSave={handleSave}
        saving={saving}
        showPreview={showPreview}
        onTogglePreview={() => setShowPreview(!showPreview)}
      />

      {message && (
        <div className={`adminblog-message ${message.type}`}>{message.text}</div>
      )}

      <div className={`adminblog-editor-layout ${showPreview ? 'with-preview' : ''}`}>
        {/* Main: Block Editor */}
        <div>
          <BlockEditor
            blocks={post.blocks || []}
            selectedBlockId={selectedBlockId}
            onSelectBlock={setSelectedBlockId}
            onUpdateBlocks={handleBlocksChange}
          />
        </div>

        {/* Right: Post Settings + Block Config (dynamic) */}
        <div className="adminblog-right-panel">
          {selectedBlock ? (
            <>
              <BlockConfigPanel
                block={selectedBlock}
                onUpdate={handleBlockUpdate}
                onDeselect={() => setSelectedBlockId(null)}
              />
              <PostSettingsPanel
                post={post}
                onChange={handlePostChange}
                collapsed
              />
            </>
          ) : (
            <PostSettingsPanel
              post={post}
              onChange={handlePostChange}
            />
          )}
        </div>

        {/* Preview Panel (optional) */}
        {showPreview && (
          <div className="adminblog-preview-panel">
            <h4>Preview</h4>
            <div className="adminblog-preview-content">
              <h2 style={{ fontFamily: "'Canela', Georgia, serif", fontSize: 20, fontWeight: 300, marginBottom: 16 }}>
                {post.title}
              </h2>
              {sortedBlocks.map(block => (
                <BlogBlockRenderer key={block.id} block={block} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
